import React, { useRef, useCallback, useState, useEffect } from 'react';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { addDays } from 'date-fns';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiSave } from 'react-icons/fi';

import api from 'services/api';
import getValidationErrors from 'services/getValidationErros';
import AppError from 'utils/AppError';
import { show } from 'store/modules/dialog/actions';

import Card from 'components/Card';

import { Container, Form, FormTelao } from './styles';
import Button from 'components/Button';
import TextArea from 'components/TextArea';
import DatePicker from 'components/DatePicker';
import Tooltip from 'components/Tooltip';
import { TipoSolicitacaoTelao } from 'utils/variables';
import Spinner from 'components/Spinner';

// configuração do validator
const schema = Yup.object().shape({
  prazo: Yup.date()
    .typeError('Campo obrigatória')
    .required('Campo obrigatória'),
  observacao: Yup.string().required('Campo Obrigatório'),
  justificativa: Yup.string().when('$telao', {
    is: telao => telao === TipoSolicitacaoTelao.NOVACRIACAO,
    then: Yup.string().required('Campo Obrigatório'),
  }),
});

export default function SolicitacaoTelaoStore() {
  // react-router-dom
  const navigate = useNavigate();

  const formRef = useRef<FormHandles>(null);

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const { solicitacaoId } = useParams<{ solicitacaoId: string }>();
  const { telao } = useParams<{ telao: string }>();

  const dispatch = useDispatch();

  const handleMessage = useCallback(
    ({ contentHTML, redirectTo, type }) => {
      const option = {
        type: type,
        confirmButtonText: '',
        contentHTML,
        timer: 3000,
        cancelButtonText: 'NÃO',
        redirectTo,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  const handleSubmit = useCallback(
    async data => {
      try {
        if (formRef) {
          setLoadingSubmit(true);

          if (formRef.current) formRef.current.setErrors({});
          await schema.validate(data, {
            abortEarly: false,
            context: { telao },
          });

          const [responsePost, responsePatch] = await Promise.all([
            api.post(`/solicitacaotelao`, {
              ...data,
              solicitacaopromo_id: solicitacaoId,
              telao,
            }),
            api.patch(`/solicitacaopromo/${solicitacaoId}/telao`, {
              telao,
            }),
          ]);
          if (
            responsePost &&
            responsePost.data &&
            responsePatch &&
            responsePatch.data
          )
            handleMessage({
              contentHTML: `<h3>Solicitação de Telão adicionada!</h3> <p>Você será redirecionado para a lista de solicitações em 3 segundos</p>`,
              redirectTo: `/solicitacaopromo`,
              type: 'success',
            });
          else toast.error('Falha ao salvar');
        }
      } catch (err) {
        console.log(err);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRef.current) formRef.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingSubmit(false);
    },
    [handleMessage, solicitacaoId],
  );

  useEffect(() => {
    const options = {
      type: 'warning',
      contentHTML: `<h2>ATENÇÃO<h2><br /><h4>Nessa etapa, você incluirá informações para filmes específicos, voltados às grandes parcerias. <br />
      É fundamental que você alinhe com a coordenação de Promo antes de preencher, combinado?</h4>`,
      allowOutsideClick: false,
      cancelButtonText: 'OK',
      showConfirmButton: false,
    };
    if (telao === TipoSolicitacaoTelao.NOVACRIACAO) {
      dispatch(show(options));
    }
  }, []);

  return (
    <Container>
      <Card>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <div>
            <header>
              <h4>
                SOLICITAÇÕES TELÃO -{' '}
                {telao &&
                  telao
                    .replace('novacriacao', 'NOVA CRIAÇÃO')
                    .replace('outroformato', 'OUTRO FORMATO')}
              </h4>
            </header>

            <FormTelao>
              <div>
                <section>
                  <h5 id="prazo">Qual é o prazo?</h5>
                </section>
                <Tooltip target="prazo">
                  Importante: Indique quando o vídeo deverá estar em suas mãos,
                  e não a data do seu evento. *
                </Tooltip>
                <DatePicker
                  placeholderText="Selecione a data"
                  minDate={
                    telao === TipoSolicitacaoTelao.NOVACRIACAO
                      ? addDays(new Date(), 15)
                      : addDays(new Date(), 7)
                  }
                  name="prazo"
                  disabled={false}
                />
              </div>
            </FormTelao>
            <div>
              <section>
                <h5>
                  Descreva o tamanho (resolução) e duração permitida do vídeo.
                  Para spots (áudio), indique somente a duração. *
                </h5>
              </section>
              <TextArea
                height="calc(100vh - 900px)"
                errorField=""
                icon=""
                name="observacao"
                max={500}
                placeholder="Escreva aqui"
              />
            </div>
            {telao === TipoSolicitacaoTelao.NOVACRIACAO && (
              <div>
                <section>
                  <h5>
                    Por que o 'filme neutro' não funcionaria para seu evento? *
                  </h5>
                </section>
                <TextArea
                  height="calc(100vh - 800px)"
                  errorField=""
                  icon=""
                  id="justificativa"
                  name="justificativa"
                  max={100}
                  placeholder="Conta aqui"
                />
              </div>
            )}
          </div>
          <div className="ButtonGroup">
            <Button
              color="primary"
              onClick={e => {
                e.preventDefault();
                navigate(-1);
              }}
            >
              VOLTAR
              <FiArrowLeft />
            </Button>
            <div>
              <Button className="submitButton" type="submit" color="primary">
                <FiSave />
                {!loadingSubmit ? 'SALVAR' : <Spinner type="ThreeDots" />}
              </Button>
            </div>
          </div>
        </Form>
      </Card>
    </Container>
  );
}
